import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import {
  budgetAllocationDetailsExportQueryOptions,
  budgetAllocationDetailsRawExportQueryOptions,
  budgetAllocationsSearchSchema,
  useBudgetAllocationSummary,
  useCreateBudgetAllocation,
} from '@/hooks/queries/crt/useBudgetAllocations'
import { queryClient } from '@/main'
import { Button } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BudgetAllocationDialog } from './-components/BudgetAllocationDialog'
import BudgetAllocationsDataTable from './-components/BudgetAllocationsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/insights/budget-allocations/',
)({
  component: RouteComponent,
  validateSearch: (search) => budgetAllocationsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const search = Route.useSearch()
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const { data: summary } = useBudgetAllocationSummary()
  const rowCount = summary?.rowCount ?? 0

  const { mutate, isPending } = useCreateBudgetAllocation({
    handleOnSuccess: () => setIsCreateOpen(false),
  })

  return (
    <PageContainer>
      <Spread>
        <VStack gap="xs">
          <PageHeader>{t('budgetAllocations.pageHeader')}</PageHeader>
          <Text tone="muted">{t('budgetAllocations.pageDescription')}</Text>
        </VStack>

        <HStack gap="sm" align="center">
          <ExportCsvMenu
            items={[
              <ExportCsvMenuItem
                label={t('export.viewData', { ns: 'common' })}
                description={t('export.viewDataDescription', { ns: 'common' })}
                getCsvExport={() =>
                  queryClient.ensureQueryData(budgetAllocationDetailsExportQueryOptions(search))
                }
                rowCount={rowCount}
              />,
              <ExportCsvMenuItem
                label={t('export.rawData', { ns: 'common' })}
                description={t('export.rawDataDescription', { ns: 'common' })}
                getCsvExport={() =>
                  queryClient.ensureQueryData(budgetAllocationDetailsRawExportQueryOptions)
                }
                rowCount={rowCount}
              />,
            ]}
          />
          <PermissionsGuard required={['crt_budget_allocations_create']}>
            <Button type="button" onClick={() => setIsCreateOpen(true)}>
              <PlusIcon className="size-4 shrink-0" />
              {t('budgetAllocations.createButton')}
            </Button>
          </PermissionsGuard>
        </HStack>
      </Spread>

      <Card>
        <CardHeader>
          <CardTitle>{t('budgetAllocations.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <BudgetAllocationsDataTable />
        </CardContent>
      </Card>

      <BudgetAllocationDialog
        mode="create"
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        isPending={isPending}
        onSubmit={(values) => mutate(values as Parameters<typeof mutate>[0])}
      />
    </PageContainer>
  )
}
